import { useMemo } from "react";
import { SiteId, AssetCameraKey } from "@visioni/a3s-schema";
import { useAuthorizer } from "./useAuthorizer";

export interface CameraPermissions {
  canViewLivestream: boolean;
  canViewClips: boolean;
  canSetNickname: boolean;
  canManageAlarmSchedules: boolean;
  isLoading: boolean;
}

export interface SitePermissions {
  canManageAllAlarmSchedules: boolean;
  isTenantAdmin: boolean;
  isLoading: boolean;
}

/**
 * Evaluates camera-level permissions using the client-side Authorizer.
 * Tenant admins are handled by the casbin model (wildcard match).
 * Returns fail-closed defaults while loading.
 */
export function useCameraPermissions(siteId: string, cameraKey: string): CameraPermissions {
  const { authorizer, userId, tenantId, isLoading } = useAuthorizer();

  return useMemo(() => {
    if (isLoading || !authorizer || !userId || !tenantId || !siteId || !cameraKey) {
      return {
        canViewLivestream: false,
        canViewClips: false,
        canSetNickname: false,
        canManageAlarmSchedules: false,
        isLoading: true,
      };
    }

    const parsedSiteId = SiteId.fromString(siteId);
    const parsedCameraKey = AssetCameraKey.fromString(cameraKey);

    return {
      canViewLivestream: authorizer.canAccessCamera(tenantId, userId, parsedSiteId, parsedCameraKey, "livestream:view"),
      canViewClips: authorizer.canAccessCamera(tenantId, userId, parsedSiteId, parsedCameraKey, "clips:view"),
      canSetNickname: authorizer.canAccessCamera(tenantId, userId, parsedSiteId, parsedCameraKey, "nickname:set"),
      canManageAlarmSchedules: authorizer.canAccessCamera(
        tenantId,
        userId,
        parsedSiteId,
        parsedCameraKey,
        "alarm-schedules:manage",
      ),
      isLoading: false,
    };
  }, [authorizer, userId, tenantId, siteId, cameraKey, isLoading]);
}

/**
 * Checks whether the user can manage alarm schedules across ALL cameras in a site.
 * Takes an array of camera keys belonging to the site.
 */
export function useSitePermissions(siteId: string, cameraKeys: string[] | undefined): SitePermissions {
  const { authorizer, userId, tenantId, isTenantAdmin, isLoading } = useAuthorizer();

  return useMemo(() => {
    if (isLoading || !authorizer || !userId || !tenantId || !siteId || cameraKeys === undefined) {
      return { canManageAllAlarmSchedules: false, isTenantAdmin: false, isLoading: true };
    }

    if (cameraKeys.length === 0) {
      return { canManageAllAlarmSchedules: true, isTenantAdmin, isLoading: false };
    }

    const parsedSiteId = SiteId.fromString(siteId);
    const canManageAll = cameraKeys.every((key) =>
      authorizer.canAccessCamera(
        tenantId,
        userId,
        parsedSiteId,
        AssetCameraKey.fromString(key),
        "alarm-schedules:manage",
      ),
    );

    return { canManageAllAlarmSchedules: canManageAll, isTenantAdmin, isLoading: false };
  }, [authorizer, userId, tenantId, siteId, cameraKeys, isTenantAdmin, isLoading]);
}

/**
 * Returns whether the current user is a tenant admin.
 * Derived from the casbin model — no separate API field needed.
 */
export function useIsTenantAdmin(): { isTenantAdmin: boolean; isLoading: boolean; isError: boolean } {
  const { isTenantAdmin, isLoading, isError } = useAuthorizer();
  return useMemo(() => ({ isTenantAdmin, isLoading, isError }), [isTenantAdmin, isLoading, isError]);
}
